import { AttendeeFormDialog } from '@/components/AttendeeFormDialog';
import { AttendeeModalityBadge } from '@/components/AttendeeModalityBadge';
import { AttendeeSourceBadge } from '@/components/AttendeeSourceBadge';
import { CopyToEventDialog } from '@/components/CopyToEventDialog';
import { ManualQrShareDialog } from '@/components/attendees/dialogs/ManualQrShareDialog';
import { SendWhatsappDialog } from '@/components/attendees/dialogs/SendWhatsappDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { useEvents } from '@/hooks/use-events';
import { apiFetch, messageFromBody } from '@/lib/api';
import { normalizeFilename } from '@/lib/filename';
import { useAuthStore } from '@/stores/auth-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import {
  type Attendee,
  Attendee as AttendeeSchema,
  type EmailLogEntry,
  EmailLogResponse,
  SendEmailResponse,
} from '@tunodo/ticketing-shared';
import {
  ArrowLeft,
  Award,
  CheckCircle2,
  Compass,
  Copy,
  FileText,
  IdCard,
  Mail,
  MessageSquare,
  Pencil,
  QrCode,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_app/attendees/$id')({
  beforeLoad: () => {
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden ver el detalle de inscritos.');
      throw redirect({ to: '/' });
    }
  },
  component: AttendeeDetailPage,
});

/**
 * Descarga un recurso binario autenticado usando el token del store.
 * No usa apiFetch porque apiFetch devuelve undefined para respuestas
 * no-JSON (líneas 62–65 de lib/api.ts).
 */
async function downloadAuthenticated(url: string, filename: string): Promise<void> {
  const token = useAuthStore.getState().token;
  const response = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!response.ok) {
    let message = response.statusText;
    try {
      message = messageFromBody(await response.json()) ?? message;
    } catch {
      // ignorar errores de parseo
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(objectUrl);
}

function AttendeeDetailPage() {
  const { id } = Route.useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { eventsMap } = useEvents();

  // ── Management dialog state ──
  const [editOpen, setEditOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const {
    data: attendee,
    isLoading,
    isError,
    error,
  } = useQuery<Attendee>({
    queryKey: ['attendee', id],
    queryFn: () =>
      apiFetch(`/api/attendees/${id}`, {
        schema: AttendeeSchema,
      }),
    retry: 1,
  });

  const sendEmailMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/attendees/${id}/send-email`, {
        method: 'POST',
        schema: SendEmailResponse,
      }),
    onSuccess: (response) => {
      toast.success(response.messageId ? `Email enviado: ${response.messageId}` : 'Email enviado');
      void queryClient.invalidateQueries({ queryKey: ['attendee', id] });
      void queryClient.invalidateQueries({ queryKey: ['attendee', id, 'email-log'] });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const sendPassportInviteMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/attendees/${id}/send-passport-invite`, {
        method: 'POST',
      }),
    onSuccess: () => {
      toast.success('Pasaporte enviado');
      void queryClient.invalidateQueries({ queryKey: ['attendee', id, 'email-log'] });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const sendCertificateMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/attendees/${id}/send-certificate`, {
        method: 'POST',
      }),
    onSuccess: () => {
      toast.success('Certificado enviado');
      void queryClient.invalidateQueries({ queryKey: ['attendee', id, 'email-log'] });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const { data: emailLogData, isLoading: isEmailLogLoading } = useQuery({
    queryKey: ['attendee', id, 'email-log'],
    queryFn: () =>
      apiFetch(`/api/attendees/${id}/email-log`, {
        schema: EmailLogResponse,
      }),
    enabled: !!attendee,
  });

  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [sendWaOpen, setSendWaOpen] = useState(false);

  // ── Management mutations ──
  const setStatusMutation = useMutation({
    mutationFn: ({ type, enabled }: { type: 'ATTENDANCE' | 'LUNCH'; enabled: boolean }) =>
      apiFetch(`/api/attendees/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ type, enabled }),
        schema: AttendeeSchema,
      }),
    onSuccess: (_, { type, enabled }) => {
      const label = type === 'ATTENDANCE' ? 'Asistencia' : 'Almuerzo';
      const action = enabled ? 'marcada' : 'revocada';
      toast.success(`${label} ${action}`);
      void queryClient.invalidateQueries({ queryKey: ['attendee', id] });
      void queryClient.invalidateQueries({ queryKey: ['attendees'] });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => apiFetch(`/api/attendees/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      toast.success('Inscrito eliminado');
      void queryClient.invalidateQueries({ queryKey: ['attendees'] });
      void navigate({ to: '/attendees', search: { page: 1, pageSize: 10 } });
    },
    onError: (err: Error) => {
      toast.error(err.message);
    },
  });

  const handleDownloadQr = async () => {
    if (!attendee) return;
    try {
      await downloadAuthenticated(
        `/api/attendees/${id}/qr.png`,
        `qr-${normalizeFilename(attendee.fullName)}.png`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al descargar el QR');
    }
  };

  const handleDownloadPdf = async () => {
    if (!attendee) return;
    try {
      await downloadAuthenticated(
        `/api/attendees/${id}/ticket.pdf`,
        `ticket-${normalizeFilename(attendee.fullName)}.pdf`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al descargar el PDF');
    }
  };

  const handleDownloadBadge = async () => {
    if (!attendee) return;
    try {
      await downloadAuthenticated(
        `/api/attendees/${id}/ticket-v2.pdf`,
        `badge-${normalizeFilename(attendee.fullName)}.pdf`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al descargar la credencial');
    }
  };

  // --- Loading state ---
  if (isLoading) {
    return (
      <main className="flex-1 p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Skeleton className="h-9 w-20" />
          <Skeleton className="h-5 w-48" />
        </div>
        <Card>
          <CardHeader>
            <Skeleton className="h-7 w-64" />
            <Skeleton className="h-5 w-32 mt-2" />
          </CardHeader>
          <CardContent className="space-y-3">
            <Skeleton className="h-4 w-full max-w-xs" />
            <Skeleton className="h-4 w-full max-w-xs" />
            <Skeleton className="h-4 w-48" />
          </CardContent>
        </Card>
        <div className="flex flex-wrap gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeleton buttons no tienen identidad
            <Skeleton key={i} className="h-9 w-36" />
          ))}
        </div>
      </main>
    );
  }

  // --- Error 404 ---
  const isNotFound = isError && (error as { status?: number })?.status === 404;
  if (isNotFound) {
    return (
      <main className="flex-1 p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to="/attendees" search={{ page: 1, pageSize: 10 }}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Volver
            </Link>
          </Button>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>Inscrito no encontrado</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              No existe ningún inscrito con el ID solicitado.
            </p>
            <Button variant="outline" size="sm" asChild>
              <Link to="/attendees" search={{ page: 1, pageSize: 10 }}>
                Ver todos los inscritos
              </Link>
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  // --- Error general ---
  if (isError) {
    return (
      <main className="flex-1 p-6 space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" asChild>
            <Link to="/attendees" search={{ page: 1, pageSize: 10 }}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Volver
            </Link>
          </Button>
        </div>
        <Card className="border-destructive/50 bg-destructive/10">
          <CardHeader>
            <CardTitle className="text-destructive">Error al cargar el inscrito</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : 'Error desconocido'}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void queryClient.invalidateQueries({ queryKey: ['attendee', id] })}
            >
              Reintentar
            </Button>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (!attendee) return null;

  return (
    <main className="flex-1 p-6 space-y-6">
      {/* Header con breadcrumb y botón volver */}
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" asChild>
          <Link to="/attendees" search={{ page: 1, pageSize: 10 }}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Volver
          </Link>
        </Button>
        <nav className="text-sm text-muted-foreground flex items-center gap-1">
          <Link
            to="/attendees"
            search={{ page: 1, pageSize: 10 }}
            className="hover:text-foreground transition-colors"
          >
            Inscritos
          </Link>
          <span>/</span>
          <span className="text-foreground font-medium">{attendee.fullName}</span>
        </nav>
      </div>

      {/* Card principal — Información */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl flex items-center gap-2 flex-wrap">
            <span>{attendee.fullName}</span>
            <AttendeeModalityBadge modality={attendee.modality} />
            <AttendeeSourceBadge source={attendee.source} />
          </CardTitle>
          <div className="flex items-center gap-2 mt-1">
            <span
              className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold font-mono bg-muted text-muted-foreground cursor-default"
              title={attendee.qrToken}
            >
              {attendee.qrToken.slice(0, 8)}&hellip;
            </span>
            <span
              className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-sm font-bold font-mono bg-primary text-primary-foreground"
              title="Código corto"
            >
              {attendee.shortCode}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          <dl className="space-y-2 text-sm">
            <div className="flex gap-2">
              <dt className="text-muted-foreground w-24 shrink-0">Email</dt>
              <dd className="font-medium">{attendee.email}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-muted-foreground w-24 shrink-0">Teléfono</dt>
              <dd className="font-medium">{attendee.phone}</dd>
            </div>
            {attendee.profesion && (
              <div className="flex gap-2">
                <dt className="text-muted-foreground w-24 shrink-0">Profesión</dt>
                <dd className="font-medium">{attendee.profesion}</dd>
              </div>
            )}
            {attendee.empresa && (
              <div className="flex gap-2">
                <dt className="text-muted-foreground w-24 shrink-0">Empresa</dt>
                <dd className="font-medium">{attendee.empresa}</dd>
              </div>
            )}
            {eventsMap[attendee.eventId] && (
              <div className="flex gap-2">
                <dt className="text-muted-foreground w-24 shrink-0">Evento</dt>
                <dd className="font-medium">{eventsMap[attendee.eventId]}</dd>
              </div>
            )}
            <div className="flex gap-2">
              <dt className="text-muted-foreground w-24 shrink-0">Código</dt>
              <dd className="font-mono font-bold tracking-widest">{attendee.shortCode}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-muted-foreground w-24 shrink-0">ID</dt>
              <dd className="font-mono text-xs text-muted-foreground">{attendee.id}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      {/* Estado del inscrito */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Estado
        </h2>
        <div className="flex gap-3">
          <div className="flex items-center gap-2 rounded-lg border p-3 flex-1">
            <span className="text-sm text-muted-foreground">Asistencia</span>
            {attendee.hasAttended ? (
              <Badge className="bg-green-100 text-green-700 border-green-200 ml-auto">
                Asistió
              </Badge>
            ) : (
              <Badge variant="secondary" className="ml-auto">
                Pendiente
              </Badge>
            )}
            <Switch
              checked={attendee.hasAttended}
              disabled={setStatusMutation.isPending}
              onCheckedChange={(checked) =>
                setStatusMutation.mutate({ type: 'ATTENDANCE', enabled: checked })
              }
              aria-label="Toggle asistencia"
            />
          </div>
          <div className="flex items-center gap-2 rounded-lg border p-3 flex-1">
            <span className="text-sm text-muted-foreground">Almuerzo</span>
            {attendee.hasClaimedLunch ? (
              <Badge className="bg-green-100 text-green-700 border-green-200 ml-auto">
                Reclamado
              </Badge>
            ) : (
              <Badge variant="secondary" className="ml-auto">
                Pendiente
              </Badge>
            )}
            <Switch
              checked={attendee.hasClaimedLunch}
              disabled={setStatusMutation.isPending}
              onCheckedChange={(checked) =>
                setStatusMutation.mutate({ type: 'LUNCH', enabled: checked })
              }
              aria-label="Toggle almuerzo"
            />
          </div>
        </div>
      </section>

      {/* Gestión del inscrito */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Gestión
        </h2>
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" className="gap-2" onClick={() => setEditOpen(true)}>
            <Pencil className="h-4 w-4" />
            Editar
          </Button>
          <Button variant="outline" className="gap-2" onClick={() => setCopyOpen(true)}>
            <Copy className="h-4 w-4" />
            Copiar a otro evento
          </Button>
          <Button
            variant="outline"
            className="gap-2 text-destructive border-destructive/50 hover:bg-destructive/10"
            onClick={() => setDeleteOpen(true)}
          >
            <Trash2 className="h-4 w-4" />
            Eliminar inscrito
          </Button>
        </div>
      </section>

      {/* Sección de acciones */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Acciones
        </h2>
        <div className="flex flex-wrap gap-3">
          {/* 1. Reenviar email */}
          <Button
            variant="outline"
            className="gap-2"
            disabled={sendEmailMutation.isPending}
            onClick={() => void sendEmailMutation.mutate()}
          >
            <Mail className="h-4 w-4" />
            {sendEmailMutation.isPending ? 'Enviando…' : 'Reenviar email'}
          </Button>

          {/* 1b. Enviar invitación pasaporte */}
          <Button
            variant="outline"
            className="gap-2"
            disabled={sendPassportInviteMutation.isPending}
            onClick={() => void sendPassportInviteMutation.mutate()}
          >
            <Compass className="h-4 w-4" />
            {sendPassportInviteMutation.isPending ? 'Enviando…' : 'Enviar invitación pasaporte'}
          </Button>

          {/* 1c. Enviar certificado */}
          <Button
            variant="outline"
            className="gap-2"
            disabled={sendCertificateMutation.isPending}
            onClick={() => void sendCertificateMutation.mutate()}
          >
            <Award className="h-4 w-4" />
            {sendCertificateMutation.isPending ? 'Enviando…' : 'Enviar certificado'}
          </Button>

          {/* 2. Descargar QR PNG */}
          <Button variant="outline" className="gap-2" onClick={() => void handleDownloadQr()}>
            <QrCode className="h-4 w-4" />
            Descargar QR
          </Button>

          {/* 3. Descargar PDF ticket */}
          <Button variant="outline" className="gap-2" onClick={() => void handleDownloadPdf()}>
            <FileText className="h-4 w-4" />
            Descargar PDF
          </Button>

          {/* 4. Descargar Credencial */}
          <Button variant="outline" className="gap-2" onClick={() => void handleDownloadBadge()}>
            <IdCard className="h-4 w-4" />
            Descargar Credencial
          </Button>

          {/* 4b. Ver credencial 3D */}
          <Button variant="outline" className="gap-2" asChild>
            <a href={`/badge?token=${attendee.qrToken}`} target="_blank" rel="noreferrer">
              <QrCode className="h-4 w-4" />
              Ver badge 3D
            </a>
          </Button>

          {/* 5. Compartir manual por WhatsApp */}
          <Button variant="outline" className="gap-2" onClick={() => setWhatsappOpen(true)}>
            <MessageSquare className="h-4 w-4" />
            Compartir manual (QR)
          </Button>

          {/* 6. Enviar boleta por WhatsApp (gateway directo) */}
          <Button variant="outline" className="gap-2" onClick={() => setSendWaOpen(true)}>
            <Send className="h-4 w-4" />
            Enviar boleta por WhatsApp
          </Button>
        </div>
      </section>

      {/* Sección historial de emails */}
      <section className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
          Historial de emails
        </h2>
        {isEmailLogLoading ? (
          <Card>
            <CardContent className="pt-4 space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton items no tienen identidad
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </CardContent>
          </Card>
        ) : !emailLogData?.logs.length ? (
          <Card>
            <CardContent className="pt-4">
              <p className="text-sm text-muted-foreground">No se ha enviado ningún email aún.</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="pt-4">
              <ul className="divide-y divide-border">
                {emailLogData.logs.map((entry: EmailLogEntry) => (
                  <li
                    key={entry.id}
                    className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      {entry.result === 'SENT' ? (
                        <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-destructive shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {entry.result === 'SENT' ? 'Enviado' : 'Fallido'}
                        </p>
                        {entry.messageId && (
                          <p
                            className="text-xs text-muted-foreground font-mono truncate"
                            title={entry.messageId}
                          >
                            {entry.messageId}
                          </p>
                        )}
                        {entry.error && (
                          <p className="text-xs text-destructive truncate" title={entry.error}>
                            {entry.error}
                          </p>
                        )}
                      </div>
                    </div>
                    <time
                      className="text-xs text-muted-foreground whitespace-nowrap"
                      dateTime={entry.sentAt}
                    >
                      {new Intl.DateTimeFormat('es-ES', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                      }).format(new Date(entry.sentAt))}
                    </time>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </section>

      {/* Management dialogs */}
      <AttendeeFormDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        mode="edit"
        attendee={attendee}
      />

      <CopyToEventDialog open={copyOpen} onOpenChange={setCopyOpen} attendee={attendee} />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Eliminar inscrito"
        description={`¿Estás seguro de que deseas eliminar a "${attendee.fullName}"? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        variant="destructive"
        onConfirm={() => deleteMutation.mutate()}
      />

      <ManualQrShareDialog open={whatsappOpen} onOpenChange={setWhatsappOpen} attendee={attendee} />

      <SendWhatsappDialog open={sendWaOpen} onOpenChange={setSendWaOpen} attendee={attendee} />
    </main>
  );
}
